import React, { useState } from "react";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherChevronDown,
  FeatherChevronRight,
  FeatherRefreshCw,
  FeatherXCircle,
} from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { GstZohoRegistration } from "../../domain/entities/CompanySettings";

interface GstZohoBlockProps {
  zoho: GstZohoRegistration | null;
  refreshing: boolean;
  onRefresh: () => void;
}

/** One label/value pair in the block. */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-caption font-caption text-subtext-color">{label}</span>
    <span className="text-body font-body text-default-font">{children}</span>
  </div>
);

/**
 * ZOUR-19 — "In Zoho Books": the GST registration as Zoho reports it.
 *
 * READ-ONLY, and that is not a simplification. Zoho's public API has no
 * GST-registration surface at all, and the endpoint its own web app uses to
 * flip an org is session + CSRF gated, so Zournal genuinely cannot do this for
 * the user (research §1). The honest design is therefore to show the state and
 * spell out the manual step — which is what the help block below does, in the
 * order the Zoho screens actually appear.
 *
 * Three states, not two. "Unknown" (Zoho unreachable) is shown as its own
 * amber state rather than collapsed into "not registered": telling a
 * registered client they are unregistered because a token expired would send
 * them to change settings that are already correct.
 *
 * The manual step sits behind a collapsed "How do I turn GST on in Zoho Books?"
 * link and appears only in the NOT-REGISTERED state (item 26). On a registered
 * org it was instructions for a job already done, and on an unreachable one we
 * cannot honestly say they are needed.
 */
export const GstZohoBlock: React.FC<GstZohoBlockProps> = ({ zoho, refreshing, onRefresh }) => {
  const unknown = !zoho || zoho.reachable !== true || zoho.registered === null;
  const registered = !unknown && zoho?.registered === true;

  // ZOUR-19 item 26: the how-to is only useful to somebody who has NOT done it.
  // On a registered org it was six lines of instructions for a job already
  // finished, and on an unreachable one it is advice we cannot say is needed.
  // Collapsed by default and not persisted: it is a one-off read, not a
  // preference worth remembering.
  const [howToOpen, setHowToOpen] = useState(false);
  const showHowTo = !unknown && !registered;

  return (
    <section className="flex w-full flex-col gap-4 border-b border-solid border-neutral-border pb-8">
      <div className="flex w-full items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">In Zoho Books</span>
          <span className="text-body font-body text-subtext-color">
            Read live from your books. Zournal cannot change this. GST is switched on inside
            Zoho Books.
          </span>
        </div>

        <Button
          variant="neutral-secondary"
          icon={<FeatherRefreshCw />}
          loading={refreshing}
          disabled={refreshing}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </div>

      <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
        <div className="flex items-center gap-2">
          {unknown ? (
            <>
              <FeatherAlertTriangle className="shrink-0 text-body font-body text-warning-700" />
              <span className="text-body-bold font-body-bold text-warning-700">
                Couldn't check with Zoho Books
              </span>
            </>
          ) : registered ? (
            <>
              <FeatherCheckCircle className="shrink-0 text-body font-body text-success-700" />
              <span className="text-body-bold font-body-bold text-success-700">
                Registered for GST
              </span>
            </>
          ) : (
            <>
              <FeatherXCircle className="shrink-0 text-body font-body text-error-700" />
              <span className="text-body-bold font-body-bold text-error-700">
                Not registered for GST
              </span>
            </>
          )}
        </div>

        {unknown && zoho?.error && (
          <span className="text-caption font-caption text-subtext-color">{zoho.error}</span>
        )}

        {!unknown && (
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
            <Fact label="GSTIN in Zoho Books">
              {zoho?.gstin ?? <span className="text-subtext-color">Not set in Zoho</span>}
            </Fact>
            <Fact label="GST registered on">
              {zoho?.registeredOn ?? (
                <span className="text-subtext-color">Not set in Zoho</span>
              )}
            </Fact>
            <Fact label="Organisation">
              {zoho?.organizationId ?? (
                <span className="text-subtext-color">Not set in Zoho</span>
              )}
            </Fact>
          </div>
        )}

        {!unknown && registered && !zoho?.registeredOn && (
          <span className="text-caption font-caption text-subtext-color">
            Zoho Books holds no registration date for this organisation, so there is nothing to
            copy from. Enter the date below yourself, and fill it in in Zoho Books too if you can.
          </span>
        )}
      </div>

      {showHowTo && (
        <div className="flex w-full flex-col gap-2">
          <button
            type="button"
            className="flex items-center gap-1 self-start text-body font-body text-brand-700 hover:underline"
            aria-expanded={howToOpen}
            onClick={() => setHowToOpen((open) => !open)}
          >
            {howToOpen ? (
              <FeatherChevronDown className="shrink-0 text-body font-body" />
            ) : (
              <FeatherChevronRight className="shrink-0 text-body font-body" />
            )}
            How do I turn GST on in Zoho Books?
          </button>

          {howToOpen && (
            <div className="flex w-full flex-col gap-1 rounded-md border border-solid border-neutral-border px-4 py-3">
              <span className="text-body font-body text-subtext-color">
                In Zoho Books, go to Settings → Taxes → GST Settings. Turn on "Is your business
                registered for GST?", enter your GSTIN and the "GST Registered On" date, then Save.
                Come back here afterwards and switch GST on below, using the same date.
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Zoho creates the GST rates for you when you save that screen. Zournal picks them up
                the first time you switch GST on here.
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
